import type { AudioTurnEvent } from '@common/audio-note/audio-note.events';
import { AUDIO_TURN_IDEMPOTENCY_HEADER, type StoredAudioAsset } from '@common/audio-note/audio-note.types';
import { API_URL } from '../constants';
import { resolveAccessToken } from './Auth';

export interface CreateVoiceTurnRequest {
  accessToken: string;
  file: Blob | File;
  personaId: string;
  chatId?: string;
  companionId?: string;
  durationMs?: number;
  idempotencyKey?: string;
  signal?: AbortSignal;
}

export interface VoiceTurnRecord {
  turnId: string;
  chatId: string;
  personaId: string;
  status: 'pending' | 'completed' | 'failed';
  transcript?: string;
  assistantText?: string;
  assistantAudio?: StoredAudioAsset;
  createdAt: string;
  updatedAt: string;
}

export async function createVoiceTurnStream(request: CreateVoiceTurnRequest): Promise<Response> {
  const token = await resolveAccessToken(request.accessToken);
  if (!token) throw new Error("Session expired");

  const form = new FormData();
  form.set('personaId', request.personaId);
  if (request.chatId) {
    form.set('chatId', request.chatId);
  }
  if (request.companionId) {
    form.set('companionId', request.companionId);
  }
  if (typeof request.durationMs === 'number') {
    form.set('durationMs', String(request.durationMs));
  }

  const file = request.file instanceof File
    ? request.file
    : new File([request.file], 'voice-note.webm', { type: request.file.type || 'audio/webm' });
  form.set('audio', file);

  const response = await fetch(`${API_URL}/audio/turns`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      ...(request.idempotencyKey ? { [AUDIO_TURN_IDEMPOTENCY_HEADER]: request.idempotencyKey } : {}),
    },
    body: form,
    signal: request.signal,
  });

  if (!response.ok) {
    const message = await readErrorMessage(response);
    throw new Error(message);
  }

  // TODO(audio-notes-ui): layer upload/progress reporting on top of this
  // request path if product needs richer pre-stream feedback.
  return response;
}

export async function consumeVoiceTurnStream(
  response: Response,
  onEvent: (event: AudioTurnEvent) => void | Promise<void>,
  signal?: AbortSignal,
): Promise<void> {
  if (!response.body) {
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      if (signal?.aborted) {
        await reader.cancel();
        break;
      }

      const { done, value } = await reader.read();
      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const frames = buffer.split('\n\n');
      buffer = frames.pop() || '';

      for (const frame of frames) {
        const parsed = parseSseEvent(frame);
        if (parsed) {
          // TODO(audio-notes-ui): if the UI needs finer-grained stream metrics
          // or retry hints, enrich handling here instead of in page components.
          await onEvent(parsed);
        }
      }
    }

    const trailingEvent = parseSseEvent(buffer);
    if (trailingEvent) {
      await onEvent(trailingEvent);
    }
  } finally {
    reader.releaseLock();
  }
}

export async function getVoiceTurn(turnId: string, accessToken: string): Promise<VoiceTurnRecord> {
  const token = await resolveAccessToken(accessToken);
  if (!token) throw new Error("Session expired");

  const response = await fetch(`${API_URL}/audio/turns/${encodeURIComponent(turnId)}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const message = await readErrorMessage(response);
    throw new Error(message);
  }

  return response.json();
}

export async function getVoiceTurnAudioBlob(turnId: string, accessToken: string): Promise<Blob> {
  const token = await resolveAccessToken(accessToken);
  if (!token) throw new Error("Session expired");

  const response = await fetch(`${API_URL}/audio/turns/${encodeURIComponent(turnId)}/audio`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const message = await readErrorMessage(response);
    throw new Error(message);
  }

  return response.blob();
}

function parseSseEvent(frame: string): AudioTurnEvent | null {
  const trimmed = frame.trim();
  if (!trimmed) {
    return null;
  }

  const dataLines = trimmed
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.startsWith('data: '))
    .map((line) => line.slice(6));

  if (dataLines.length === 0) {
    return null;
  }

  try {
    return JSON.parse(dataLines.join('\n')) as AudioTurnEvent;
  } catch {
    return null;
  }
}

async function readErrorMessage(response: Response): Promise<string> {
  try {
    const payload = await response.json();
    if (typeof payload?.error === 'string') {
      return payload.error;
    }
    if (typeof payload?.message === 'string') {
      return payload.message;
    }
    return `Voice turn request failed: ${response.status}`;
  } catch {
    return `Voice turn request failed: ${response.status}`;
  }
}
